import { feature, featureCollection } from '@turf/helpers';
import turfUnion from '@turf/union';
import debug from 'debug';
import { fromEither, fromNullable, none, Option, some } from 'fp-ts/lib/Option';
import { Coordinate } from 'ol/coordinate';
import Circle from 'ol/geom/Circle';
import { fromCircle } from 'ol/geom/Polygon';
import { getPointResolution } from 'ol/proj';
import { getApiUrl, getMapCrsName } from 'sdi/app';
import { addLayer, defaultInteraction } from 'sdi/map';
import { miniMapFailed, miniMapLoaded, MiniStep } from 'sdi/map/mini';
import { assign, assignK, dispatch } from 'sdi/shape/functions';
import {
    DirectGeometryObject,
    DirectGeometryObjectIO,
    Feature,
    FeatureCollection,
    GeometryObject,
    MultiLineString,
    MultiPoint,
    MultiPolygon,
} from 'sdi/source';
import { HarvestedLayer } from 'view/src/components/query';
import {
    addLayerData,
    loadMapLayerInfo,
    setLayout,
    updateCurrentMapInPlace,
} from 'view/src/events/app';
import { postHarvestRequest } from 'view/src/remote';
import { getMapInfoOption, getMapLayers } from '../queries/app';
import {
    findHarvestedLayer,
    getDrawing,
    getHarvesting,
} from '../queries/harvest';
import {
    getAllLinkedMapsIds,
    getLinks,
    HarvestMetadataID,
    mapName,
    withoutBookmarksOrHarvest,
} from '../queries/map';
import {
    CircleDrawing,
    geometryTypeOfShape,
    Harvesting,
    HarvestShape,
    idle,
    NonCircleDrawing,
} from '../shape/types';
import { loadMetadata } from './app';
import { setPage } from './legend';
import {
    harvestLayerInfo,
    harvestMetadata,
    removeHarvestLayer,
    resetInteraction,
} from './map';
import { generateMiniMap } from './minimap';

const logger = debug('sdi:harvest');

export const setHarvestStep = assignK('component/harvest');

const setMiniMap = (step: MiniStep) =>
    dispatch('component/harvest', hs =>
        hs.state === 'harvesting' ? { ...hs, minimap: step } : hs,
    );

export const setCircleRadius = (radius: Option<number>) =>
    dispatch('component/harvest', hs =>
        hs.state === 'circle-options' ? { ...hs, radius } : hs,
    );

const harvest = (dispatcher: (results: HarvestedLayer[]) => HarvestedLayer[]) =>
    dispatch('component/harvest', harvestState => {
        if (harvestState.state === 'harvesting')
            return {
                ...harvestState,
                results: dispatcher(harvestState.results),
            };
        else {
            logger('not harvesting, dismissing results');
            return harvestState;
        }
    });

// radius arrives in ground meters, OL Circle works in map-CRS units,
// so we need to convert at the circle center to account for
// distortion in the map-CRS
// getPointResolution gives ground meters per projection unit
// in Brussels, on Lambert 72 it's ~1, on EPSG:3857 it's the ~1.57 factor.
const getCircle = (
    center: GeometryObject,
    radiusMeters: number,
): GeometryObject => {
    const coords = center.coordinates as Coordinate;
    const metersPerUnit = getPointResolution(getMapCrsName(), 1, coords, 'm');
    const circle = new Circle(coords, radiusMeters / metersPerUnit);
    const polygon = fromCircle(circle);

    return {
        type: 'Polygon',
        coordinates: polygon.getCoordinates(),
        crs: center.crs,
    };
};

export const addFeature = (f: Feature) =>
    getDrawing().foldL(
        () => logger('invalid harvest state'),
        drawing => {
            if (drawing.shape === 'circle') {
                const center = f.geometry.coordinates as Coordinate;
                startHarvestingCircle(
                    getCircle(f.geometry, drawing.radius),
                    center,
                    drawing.radius,
                );
            } else {
                startHarvesting(drawing.shape, f.geometry);
            }
            prepareGeometryForHarvesting();
        },
    );

export const prepareGeometryForHarvesting = () => {
    const linkedList = getLinks('backward').getOrElse([]);
    getLinks('forward').map(flist =>
        linkedList
            .concat(flist)
            .map(m =>
                getMapLayers(m).map(layer => loadMetadata(layer.metadataId)),
            ),
    );
    removeHarvestLayer();
    addHarvestToMap();

    endDrawing();
};

export const request = (
    mapId: string,
    metadata: string,
    id: number,
    groupId: number,
) =>
    getHarvesting().map(({ geometry }) => {
        const layer = findHarvestedLayer(mapId, metadata).getOrElse({
            tag: 'initial',
            metadataId: metadata,
            mapId,
            id,
            groupId,
        });

        if ('initial' === layer.tag) {
            const start = harvestLoading(mapId, metadata, id, groupId);
            postHarvestRequest(getApiUrl('harvest/'), { metadata, geometry })
                .then(results =>
                    harvestLoaded(mapId, metadata, results, start, id, groupId),
                )
                .then(() => getAllLinkedMapsIds(mapId).map(loadMapLayerInfo))
                .catch(err =>
                    harvestError(mapId, metadata, `${err}`, start, id, groupId),
                );
        }
    });

/**
 * Add CRS to geojson geometry
 *
 * As of https://tools.ietf.org/html/rfc7946#section-4
 * GeoJSON does not support CRS annotation anymore, nevertheless GEOS still
 * supports it so it's the easiest way to have a specific all the way.
 *
 * @param geom
 */
const withCRS = (geom: GeometryObject) => {
    const crsName = getMapCrsName();
    return Object.assign({}, geom, {
        crs: { type: 'name', properties: { name: crsName } },
    });
};

const beginHarvest = (step: Harvesting) => {
    assign('component/harvest', step);
    const radius = step.shape === 'circle' ? some(step.radius) : none;
    generateMiniMap(step.geometry, radius)
        .then(opt =>
            opt.foldL(
                () => setMiniMap(miniMapFailed()),
                url => setMiniMap(miniMapLoaded(url)),
            ),
        )
        .catch(() => setMiniMap(miniMapFailed()));
};

export const startHarvesting = (
    shape: Exclude<HarvestShape, 'circle'>,
    geometry: GeometryObject,
) =>
    beginHarvest({
        state: 'harvesting',
        shape,
        geometry: withCRS(geometry),
        results: [],
        minimap: { step: 'loading' },
    });

export const startHarvestingCircle = (
    geometry: GeometryObject,
    center: Coordinate,
    radius: number,
) =>
    beginHarvest({
        state: 'harvesting',
        shape: 'circle',
        geometry: withCRS(geometry),
        center,
        radius,
        results: [],
        minimap: { step: 'loading' },
    });

export const resetHarvest = () => assign('component/harvest', idle);

type DrawingInit =
    | Omit<NonCircleDrawing, 'state'>
    | Omit<CircleDrawing, 'state'>;

export const startDrawing = (init: DrawingInit) => {
    setHarvestStep({ ...init, state: 'drawing' });
    assign('port/map/interaction', {
        label: 'create',
        state: { geometryType: geometryTypeOfShape(init.shape) },
    });
};

export const endDrawing = () => {
    assign('port/map/interaction', defaultInteraction());

    //harvest visible layers (of current map)
    getMapInfoOption().map(info =>
        withoutBookmarksOrHarvest(getMapLayers(info))
            .reverse()
            .map((l, id) => {
                if (l.visible) request(info.id, l.metadataId, id, 0);
            }),
    );
};

export const removeHarvest = (mid: string) =>
    harvest(hs => hs.filter(h => h.metadataId !== mid));

const filterHarvest = (mid: string) => (h: HarvestedLayer) =>
    h.metadataId !== mid;

export const harvestLoading = (
    mapId: string,
    metadataId: string,
    id: number,
    groupId: number,
) => {
    const f = filterHarvest(metadataId);
    const start = Date.now();
    harvest(hs =>
        hs
            .filter(f)
            .concat({ tag: 'loading', metadataId, start, mapId, id, groupId }),
    );
    return start;
};

export const harvestError = (
    mapId: string,
    metadataId: string,
    error: string,
    start: number,
    id: number,
    groupId: number,
) => {
    const f = filterHarvest(metadataId);
    const end = Date.now();
    harvest(hs =>
        hs.filter(f).concat({
            tag: 'error',
            metadataId,
            start,
            end,
            error,
            mapId,
            id,
            groupId,
        }),
    );
};

export const harvestLoaded = (
    mapId: string,
    metadataId: string,
    data: FeatureCollection,
    start: number,
    id: number,
    groupId: number,
) => {
    const f = filterHarvest(metadataId);
    const end = Date.now();
    harvest(hs =>
        hs.filter(f).concat({
            tag: 'loaded',
            metadataId,
            start,
            end,
            data,
            mapId,
            id,
            groupId,
        }),
    );
};

export const endHarvest = () => {
    resetHarvest();
    resetInteraction();
    removeHarvestLayer();
};

export const printHarvest = () => {
    setLayout('query-print');
    window.setTimeout(() => window.print(), 300);
    window.setTimeout(() => setLayout('map-and-info'), 1000);
    // window.setTimeout(endHarvest, 1000);
};

export const addHarvestToMap = () => {
    harvestMetadata().map(meta =>
        dispatch('data/datasetMetadata', state => ({
            ...state,
            [HarvestMetadataID]: meta,
        })),
    );
    harvestLayerInfo().map(({ info, layer, data }) => {
        updateCurrentMapInPlace(map => map.layers.push(layer.id));
        addLayerData(layer);
        addLayer(mapName, info, data);
    });
};

const getShape = (
    geometry: GeometryObject,
): Exclude<HarvestShape, 'circle'> => {
    switch (geometry.type) {
        case 'Point':
        case 'MultiPoint':
            return 'point';
        case 'LineString':
        case 'MultiLineString':
            return 'line';
        case 'Polygon':
        case 'MultiPolygon':
            return 'polygon';
    }
};

const toMulti = (
    geom: DirectGeometryObject,
): MultiPoint | MultiLineString | MultiPolygon => {
    switch (geom.type) {
        case 'Point':
            return {
                ...geom,
                type: 'MultiPoint',
                coordinates: [geom.coordinates],
            };
        case 'LineString':
            return {
                ...geom,
                type: 'MultiLineString',
                coordinates: [geom.coordinates],
            };
        case 'Polygon':
            return {
                ...geom,
                type: 'MultiPolygon',
                coordinates: [geom.coordinates],
            };
        case 'MultiPoint':
        case 'MultiLineString':
        case 'MultiPolygon':
            return geom;
    }
};

// https://turfjs.org/docs/api/union
const unionPolygons = (a: MultiPolygon, b: MultiPolygon) =>
    fromNullable(turfUnion(featureCollection([feature(a), feature(b)])))
        .chain(f => fromEither(DirectGeometryObjectIO.decode(f.geometry)))
        .getOrElse({ ...a, coordinates: a.coordinates.concat(b.coordinates) });

const mergeTwoGeometries = (
    a: DirectGeometryObject,
    b: DirectGeometryObject,
): DirectGeometryObject => {
    const multiA = toMulti(a);
    const multiB = toMulti(b);

    switch (multiA.type) {
        case 'MultiPoint':
            return multiB.type === 'MultiPoint'
                ? {
                      ...multiA,
                      coordinates: multiA.coordinates.concat(
                          multiB.coordinates,
                      ),
                  }
                : multiA;
        case 'MultiLineString':
            return multiB.type === 'MultiLineString'
                ? {
                      ...multiA,
                      coordinates: multiA.coordinates.concat(
                          multiB.coordinates,
                      ),
                  }
                : multiA;
        case 'MultiPolygon':
            return multiB.type === 'MultiPolygon'
                ? unionPolygons(multiA, multiB)
                : multiA;
    }
};

export const mergeGeometries = (
    geometries: DirectGeometryObject[],
): Option<DirectGeometryObject> => {
    if (geometries.length === 0) return none;
    if (geometries.length === 1) return some(geometries[0]);

    return some(geometries.reduce(mergeTwoGeometries));
};

export const harvestFromGeometry = (geometry: DirectGeometryObject) => {
    endHarvest();
    setLayout('map-and-info');
    startHarvesting(getShape(geometry), geometry);
    prepareGeometryForHarvesting();
    setPage('spatial-filter');
};

logger('loaded');
